import { Model } from './model';
import { TransformStringToDayJS } from './utils';
import dayjs from 'dayjs';
import { Type } from 'class-transformer';
import { BankTransactionBase } from './bank_transaction';
import { Partner } from './partner';
import { Remittance } from './remittance';
import type { AgentMetrics, AgentParsingErrorCode } from './types';
import { PaymentAllocation } from './payment_allocation';
export { PaymentAllocation };

export type PaymentScoreLevel = 'pass' | 'fail' | 'direct' | 'skipped';
export type PaymentScoreSeverity = 'clear' | 'blocker' | 'concern';

export interface PaymentScoreBreakdownItem {
  rank: number;
  level: PaymentScoreLevel;
  severity: PaymentScoreSeverity;
  criterion: string;
  justification?: string;
}

export interface PaymentScore {
  value: number;
  breakdown: PaymentScoreBreakdownItem[];
  scored_at: string;
  summary?: string;
  summary_caveat?: string;
  severity_summary: {
    clear: number;
    blocker: number;
    concern: number;
  };
}

export interface AutoPostBlocker {
  level: string;
  criterion: string;
  justification: string;
  default_severity: string;
  effective_severity: string;
}

export interface AutoPostDecision {
  reason: string;
  profile: string;
  blocked_by: AutoPostBlocker[];
  should_post: boolean;
}

export interface RemittanceSuggestionDecision {
  // Whether the cash-app agent flags this payment as a good remittance-request
  // candidate: a confident partner with an unclear allocation and no remittance
  // attached. Drives the request-remittance highlight in the workspace.
  should_suggest: boolean;
  // Machine-readable code: 'suggest', 'no_score', 'score_stale', or
  // 'suppressed:<cause>'. Kept for tooling/tooltips; not surfaced as copy.
  reason: string;
  partner_strength: string;
  open_invoice_count: number;
  allocation_signals: string[];
  disqualifiers: string[];
}

export class Payment extends Model {
  id: string;

  agent_confidence_reason: string | null;

  agent_confidence_score: number | null;

  agent_error_code: AgentParsingErrorCode | null;

  agent_uncertainties: string[];

  allocation_status: string;

  @Type(() => PaymentAllocation)
  allocations: PaymentAllocation[];

  amount: number;

  amount_refunded: number;

  approved: boolean;

  @TransformStringToDayJS()
  approved_at: dayjs.Dayjs | null;

  @Type(() => BankTransactionBase)
  bank_transaction: BankTransactionBase | null;

  bank_transaction_id: string | null;

  billing_email: string | null;

  creation_strategy: 'automatic' | 'manual' | 'remote' | 'suggested';

  currency: string;

  currency_rate: number | null;

  description: string | null;

  finalized_by_id: string | null;

  notes: string | null;

  payment_method: string | null;

  @TransformStringToDayJS()
  initiated_at: dayjs.Dayjs | null;

  partner_id: string;

  @Type(() => Partner)
  partner: Partner;

  posted: boolean;

  @TransformStringToDayJS()
  posted_at: dayjs.Dayjs | null;

  posting_error: string | null;

  receipt_url: string | null;

  reference_number: string | null;

  remittance: Remittance | null;

  @Type(() => Remittance)
  remittances: Remittance[];

  remote_id: string | null;

  @TransformStringToDayJS()
  timestamp: dayjs.Dayjs | null;

  statement_descriptor: string | null;

  stripe_payment_intent_id: string | null;

  status: string | null;

  score: PaymentScore | null;

  score_invalidated_at: string | null;

  auto_post_decision: AutoPostDecision | null;

  remittance_suggestion_decision: RemittanceSuggestionDecision | null;

  approved_via: string | null;

  get agentMetrics(): AgentMetrics | null {
    return this.agent_confidence_score !== null || this.agent_error_code
      ? {
          agentConfidenceReason: this.agent_confidence_reason,
          agentConfidenceScore: this.agent_confidence_score,
          agentErrorCode: this.agent_error_code,
          agentUncertainties: this.agent_uncertainties,
        }
      : null;
  }

  get totalAllocated(): number {
    let total = 0;
    this.allocations.forEach((allocation) => (total += allocation.amount));
    return total;
  }

  get totalUnallocated(): number {
    return this.amount - this.totalAllocated;
  }

  get agentConfidenceLevel(): 'high' | 'medium' | 'low' | null {
    return this.agent_confidence_score === null
      ? null
      : this.agent_confidence_score >= 95
        ? 'high'
        : this.agent_confidence_score >= 85
          ? 'medium'
          : 'low';
  }

  get isForeignExchange(): boolean {
    // A payment is FX if the bank transaction currency differs from payment currency
    // Example: Bank receives 1000 USD, but payment is allocated in EUR
    return !!this.bank_transaction?.currency && this.bank_transaction.currency !== this.currency;
  }
}
